import React, { useState } from 'react';

const defaultPolicy = {
  enabled: true,
  defaultMinimumPercentage: 75,
  strictEnforcement: true,
  autoblock: false,
  blockAfterDays: 0,
  allowedStatuses: ['present', 'absent', 'late', 'on-duty', 'other'],
  modificationWindowMinutes: 60,
  requireAdminApprovalForLateMarking: true,
  enableNotifications: true,
  customReasons: [],
  allowFacultyEdit: true
};

const allowedStatusOptions = [
  { value: 'present', label: 'Present' },
  { value: 'absent', label: 'Absent' },
  { value: 'late', label: 'Late' },
  { value: 'on-duty', label: 'On Duty' },
  { value: 'other', label: 'Other' }
];

export default function AttendancePolicyConfig({ policy, onUpdate, loading, disabled, gradingRulesConfigured = true }) {
  const [localPolicy, setLocalPolicy] = useState(policy || defaultPolicy);
  const [showGradingWarning, setShowGradingWarning] = useState(false);

  const handleChange = (field, value) => {
    // If toggling autoblock off, set blockAfterDays to 0
    if (field === 'autoblock' && value === false) {
      setLocalPolicy(prev => ({ ...prev, autoblock: false, blockAfterDays: 0 }));
    } else {
      setLocalPolicy(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleStatusChange = (status) => {
    setLocalPolicy(prev => {
      const allowedStatuses = Array.isArray(prev.allowedStatuses) ? prev.allowedStatuses : [];
      const exists = allowedStatuses.includes(status);
      return {
        ...prev,
        allowedStatuses: exists
          ? allowedStatuses.filter(s => s !== status)
          : [...allowedStatuses, status]
      };
    });
  };

  const handleCustomReasonChange = (idx, value) => {
    setLocalPolicy(prev => {
      const updated = [...prev.customReasons];
      updated[idx] = value;
      return { ...prev, customReasons: updated };
    });
  };

  const addCustomReason = () => {
    setLocalPolicy(prev => ({ ...prev, customReasons: [...prev.customReasons, ''] }));
  };

  const removeCustomReason = (idx) => {
    setLocalPolicy(prev => {
      const updated = [...prev.customReasons];
      updated.splice(idx, 1);
      return { ...prev, customReasons: updated };
    });
  };

  const handleSave = () => {
    // Check if grading rules are configured before saving attendance policy
    if (!gradingRulesConfigured) {
      setShowGradingWarning(true);
      setTimeout(() => setShowGradingWarning(false), 5000);
      return;
    }
    if (onUpdate) onUpdate(localPolicy);
  };

  const inputClasses = "w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-700 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none disabled:opacity-50";
  const labelClasses = "block text-sm font-bold text-gray-700 mb-1.5 flex items-center gap-1.5";

  return (
    <div className="space-y-8">
      {/* Grading Rules Warning Banner */}
      {showGradingWarning && (
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-xl p-4 animate-in slide-in-from-top-2 duration-300">
          <div className="flex items-start gap-3">
            <div className="text-amber-600 text-xl">⚠️</div>
            <div className="flex-1">
              <h4 className="font-bold text-amber-800 text-sm">Grading Rules Not Configured</h4>
              <p className="text-amber-700 text-sm mt-1">
                Please configure your grading rules first before setting up attendance policy. 
                Grading rules define passing percentages and assessment criteria.
              </p>
              <button 
                onClick={() => setShowGradingWarning(false)}
                className="mt-2 text-xs text-amber-600 hover:text-amber-700 font-medium"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            Attendance Protocol
            {!localPolicy.enabled && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full uppercase tracking-wider">Disabled</span>}
          </h3>
          <p className="text-gray-500 text-sm mt-1">Configure the behavioral guardrails for institutional tracking.</p>
        </div>
        <div className="flex items-center gap-3 bg-gray-50 px-4 py-2 rounded-2xl border border-gray-100">
          <span className="text-sm font-medium text-gray-600">Active Policy</span>
          <button
            onClick={() => handleChange('enabled', !localPolicy.enabled)}
            disabled={disabled || loading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${localPolicy.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${localPolicy.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      {localPolicy.enabled ? (
        <div className="space-y-8 animate-in fade-in slide-in-from-top-2 duration-300">
          
          {/* Info Banner */}
          <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-5 flex gap-4">
            <div className="bg-emerald-100 text-emerald-600 p-2 rounded-xl h-fit">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h4 className="text-emerald-900 font-bold text-sm uppercase tracking-tight">Audit & Control Workflow</h4>
              <p className="text-emerald-700/80 text-sm mt-1 leading-relaxed">
                When a session is blocked, <strong>only Tenant Admins</strong> can grant overrides. All modifications are timestamped and logged for institutional transparency.
              </p>
            </div>
          </div>

          {/* Configuration Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div className="space-y-6">
              <section>
                <label className={labelClasses}>
                  <input
                    type="checkbox"
                    checked={localPolicy.autoblock ?? false}
                    disabled={disabled || loading}
                    onChange={e => handleChange('autoblock', e.target.checked)}
                    className="mr-2 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  Enable Auto Block Attendance
                </label>
                {localPolicy.autoblock ? (
                  <div className="mt-3">
                    <label className={labelClasses}>
                      Block Attendance After
                      <span className="text-gray-400 text-[10px] bg-gray-100 px-1.5 rounded uppercase">Days</span>
                    </label>
                    <input
                      type="number" min={1} max={30}
                      value={localPolicy.blockAfterDays}
                      disabled={disabled || loading}
                      onChange={e => handleChange('blockAfterDays', Number(e.target.value))}
                      className={inputClasses}
                      placeholder="e.g. 2"
                    />
                    <p className="text-[11px] text-gray-400 mt-2">Days before a session becomes read-only for faculty.</p>
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-gray-400">Auto block is disabled. Attendance will not be blocked automatically.</div>
                )}
              </section>

              <section>
                <label className={labelClasses}>
                  Required Attendance Percentage
                  <span className="text-gray-400 text-[10px] bg-gray-100 px-1.5 rounded uppercase">Minimum</span>
                </label>
                <div className="relative">
                  <input 
                    type="number" min={0} max={100}
                    value={localPolicy.defaultMinimumPercentage}
                    disabled={disabled || loading}
                    onChange={e => handleChange('defaultMinimumPercentage', Number(e.target.value))}
                    className={inputClasses}
                    placeholder="e.g. 75"
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-bold">%</div>
                </div>
                <p className="text-[11px] text-gray-400 mt-2">Students below this percentage will be marked ineligible.</p>
              </section>

              <section>
                <label className={labelClasses}>
                  Modification Window
                  <span className="text-gray-400 text-[10px] bg-gray-100 px-1.5 rounded uppercase">Minutes</span>
                </label>
                <input 
                  type="number" min={1} max={1440} 
                  value={localPolicy.modificationWindowMinutes}
                  disabled={disabled || loading}
                  onChange={e => handleChange('modificationWindowMinutes', Number(e.target.value))}
                  className={inputClasses}
                  placeholder="e.g. 60"
                />
                <p className="text-[11px] text-gray-400 mt-2">Time allowed for faculty to correct accidental entries.</p>
              </section>
            </div>

            <div className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100 space-y-4">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Rule Enforcement</h4>
              
              <label className="flex items-start gap-3 cursor-pointer group">
                <input type="checkbox" checked={localPolicy.strictEnforcement} 
                  disabled={disabled || loading}
                  onChange={e => handleChange('strictEnforcement', e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                <div>
                  <span className="block text-sm font-bold text-gray-700 group-hover:text-emerald-700 transition-colors">Strict Enforcement</span>
                  <span className="block text-xs text-gray-500">Zero exceptions without administrative digital signature.</span>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input type="checkbox" checked={localPolicy.requireAdminApprovalForLateMarking} 
                  disabled={disabled || loading}
                  onChange={e => handleChange('requireAdminApprovalForLateMarking', e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                <div>
                  <span className="block text-sm font-bold text-gray-700 group-hover:text-emerald-700 transition-colors">Admin Approval Required</span>
                  <span className="block text-xs text-gray-500">Unlocking expired sessions requires high-level clearance.</span>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input type="checkbox" checked={localPolicy.enableNotifications} 
                  disabled={disabled || loading}
                  onChange={e => handleChange('enableNotifications', e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                <div>
                  <span className="block text-sm font-bold text-gray-700 group-hover:text-emerald-700 transition-colors">System Notifications</span>
                  <span className="block text-xs text-gray-500">Notify parties when attendance windows are expiring.</span>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input type="checkbox" checked={localPolicy.allowFacultyEdit ?? true}
                  disabled={disabled || loading}
                  onChange={e => handleChange('allowFacultyEdit', e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                <div>
                  <span className="block text-sm font-bold text-gray-700 group-hover:text-emerald-700 transition-colors">Allow Faculty Edit</span>
                  <span className="block text-xs text-gray-500">Allow faculty to edit attendance within the allowed window.</span>
                </div>
              </label>
            </div>
          </div>

          {/* Allowed Statuses Section */}
          <section>
            <label className={labelClasses}>Allowed Attendance Statuses</label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {allowedStatusOptions.map(option => (
                <label key={option.value} className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={(localPolicy.allowedStatuses || []).includes(option.value)}
                    disabled={disabled || loading}
                    onChange={() => handleStatusChange(option.value)}
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700 capitalize">{option.label}</span>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-2">Select which attendance statuses faculty can mark.</p>
          </section>

          {/* Custom Reasons Section */}
          <section className="bg-white rounded-3xl border border-gray-100 p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <label className="text-sm font-bold text-gray-700 uppercase tracking-tight">Custom Exception Reasons</label>
                <p className="text-xs text-gray-400 mt-0.5">Additional reasons for late/absence marking</p>
              </div>
              <button 
                type="button" 
                onClick={addCustomReason} 
                disabled={disabled || loading}
                className="text-emerald-600 hover:text-emerald-700 text-xs font-bold flex items-center gap-1 px-3 py-1.5 rounded-lg border border-emerald-200 hover:bg-emerald-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/></svg>
                Add Custom Reason
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(localPolicy.customReasons || []).map((reason, idx) => (
                <div key={idx} className="flex items-center gap-2 group animate-in zoom-in-95 duration-200">
                  <input 
                    type="text" 
                    value={reason} 
                    disabled={disabled || loading}
                    onChange={e => handleCustomReasonChange(idx, e.target.value)}
                    placeholder="e.g. Medical Emergency"
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-700 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none disabled:opacity-50" 
                  />
                  <button 
                    type="button" 
                    onClick={() => removeCustomReason(idx)} 
                    disabled={disabled || loading}
                    className="p-2 text-gray-300 hover:text-red-500 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                </div>
              ))}
              {(localPolicy.customReasons || []).length === 0 && (
                <div className="md:col-span-2 text-center py-6 border-2 border-dashed border-gray-100 rounded-2xl text-gray-400 text-sm">
                  No custom reasons defined. Faculty will use default system reasons.
                </div>
              )}
            </div>
          </section>

          {/* Policy Summary Card */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-5">
            <h4 className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-3">Current Policy Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-xs text-emerald-600">Required Attendance</div>
                <div className="font-bold text-gray-900">{localPolicy.defaultMinimumPercentage}%</div>
              </div>
              <div>
                <div className="text-xs text-emerald-600">Auto-block</div>
                <div className="font-bold text-gray-900">{localPolicy.autoblock ? `After ${localPolicy.blockAfterDays} days` : 'Disabled'}</div>
              </div>
              <div>
                <div className="text-xs text-emerald-600">Edit Window</div>
                <div className="font-bold text-gray-900">{localPolicy.modificationWindowMinutes} min</div>
              </div>
              <div>
                <div className="text-xs text-emerald-600">Strict Mode</div>
                <div className="font-bold text-gray-900">{localPolicy.strictEnforcement ? 'Active' : 'Inactive'}</div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="py-20 text-center">
          <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/></svg>
          </div>
          <h4 className="text-gray-900 font-bold text-lg">Policy Enforcement is Inactive</h4>
          <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
            Attendance tracking will not be restricted by institutional rules. 
            Enable the policy above to activate attendance requirements.
          </p>
        </div>
      )}

      {/* Save Action with Validation */}
      <div className="pt-6 border-t border-gray-100 flex flex-col sm:flex-row justify-end items-center gap-4">
        {!gradingRulesConfigured && localPolicy.enabled && (
          <div className="flex items-center gap-2 text-amber-600 text-xs bg-amber-50 px-3 py-2 rounded-lg">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>Configure grading rules first</span>
          </div>
        )}
        <button
          onClick={handleSave}
          disabled={disabled || loading || (!gradingRulesConfigured && localPolicy.enabled)}
          className={`bg-emerald-600 hover:bg-emerald-700 text-white px-10 py-3 rounded-2xl font-bold shadow-lg shadow-emerald-200 transition-all active:scale-95 disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed`}
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              Processing...
            </div>
          ) : (
            'Deploy Attendance Policy'
          )}
        </button>
      </div>
    </div>
  );
}